import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  Search,
  BookOpen,
  Eye,
  Edit3,
  Trash2,
  Globe,
  Lock,
  Tag,
  BarChart3,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import { apiClient } from '@/lib/api-client'
import { useToast } from '@/hooks/use-toast'

// ============================================
// Types
// ============================================

interface Runbook {
  id: string
  title: string
  description: string | null
  category: string | null
  content?: string
  tags: string[]
  version: number
  isPublished: boolean
  createdBy: string
  createdAt: string
  updatedAt: string
}

interface RunbookStats {
  total: number
  published: number
  draft: number
  byCategory: Array<{ category: string; count: number }>
  recentlyUpdated: Array<{ id: string; title: string; updatedAt: string }>
}

// ============================================
// Helpers
// ============================================

const categoryColors: Record<string, string> = {
  networking: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  security: 'bg-red-500/10 text-red-500 border-red-500/20',
  server: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
  cloud: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/20',
  desktop: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
  email: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
  backup: 'bg-green-500/10 text-green-500 border-green-500/20',
  documentation: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
}

const categoryLabels: Record<string, string> = {
  networking: 'Networking',
  security: 'Security',
  server: 'Server',
  cloud: 'Cloud',
  desktop: 'Desktop',
  email: 'Email',
  backup: 'Backup',
  documentation: 'Documentation',
}

const allCategories = ['networking', 'security', 'server', 'cloud', 'desktop', 'email', 'backup', 'documentation']

function categoryBadge(category: string | null) {
  if (!category) return null
  const color = categoryColors[category] || ''
  return <Badge className={color}>{categoryLabels[category] || category}</Badge>
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function formatRelativeDate(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return formatDate(dateStr)
}

/**
 * Simple markdown-to-HTML renderer for reading view.
 * Handles headings, bold, italic, code blocks, inline code, links, lists, and paragraphs.
 */
function renderMarkdown(content: string): string {
  let html = content
    // Code blocks (fenced)
    .replace(/```(\w*)\n([\s\S]*?)```/g, '<pre class="bg-muted rounded p-3 my-2 overflow-x-auto text-sm"><code>$2</code></pre>')
    // Headers
    .replace(/^### (.+)$/gm, '<h3 class="text-base font-semibold mt-4 mb-2">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 class="text-lg font-semibold mt-4 mb-2">$1</h2>')
    .replace(/^# (.+)$/gm, '<h1 class="text-xl font-bold mt-4 mb-2">$1</h1>')
    // Bold and italic
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Inline code
    .replace(/`(.+?)`/g, '<code class="bg-muted px-1 py-0.5 rounded text-sm">$1</code>')
    // Links
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-primary underline" target="_blank" rel="noopener">$1</a>')
    // Unordered lists
    .replace(/^- (.+)$/gm, '<li class="ml-4 list-disc">$1</li>')
    // Line breaks to paragraphs
    .replace(/\n\n/g, '</p><p class="my-2">')
    .replace(/\n/g, '<br />')

  return `<p class="my-2">${html}</p>`
}

// ============================================
// Create/Edit Runbook Dialog
// ============================================

function RunbookEditorDialog({ runbook, onClose }: { runbook?: Runbook; onClose?: () => void }) {
  const [open, setOpen] = useState(!!runbook)
  const [title, setTitle] = useState(runbook?.title || '')
  const [description, setDescription] = useState(runbook?.description || '')
  const [category, setCategory] = useState(runbook?.category || '')
  const [content, setContent] = useState(runbook?.content || '')
  const [tagsInput, setTagsInput] = useState(runbook?.tags?.join(', ') || '')
  const [isPublished, setIsPublished] = useState(runbook?.isPublished || false)
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const isEdit = !!runbook

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post('/runbooks', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['runbooks'] })
      toast({ title: 'Runbook created', description: 'New runbook has been added' })
      handleClose()
    },
    onError: () => toast({ title: 'Error', description: 'Failed to create runbook', variant: 'destructive' }),
  })

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.put(`/runbooks/${runbook?.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['runbooks'] })
      toast({ title: 'Runbook updated', description: 'Changes have been saved' })
      handleClose()
    },
    onError: () => toast({ title: 'Error', description: 'Failed to update runbook', variant: 'destructive' }),
  })

  function handleClose() {
    setOpen(false)
    if (onClose) onClose()
  }

  function handleSave() {
    const tags = tagsInput
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)

    const data: Record<string, unknown> = {
      title,
      description: description || undefined,
      category: category || undefined,
      content,
      tags,
      isPublished,
    }

    if (isEdit) {
      updateMutation.mutate(data)
    } else {
      createMutation.mutate(data)
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  const dialogContent = (
    <DialogContent className="max-w-3xl max-h-[85vh]">
      <DialogHeader>
        <DialogTitle>{isEdit ? 'Edit Runbook' : 'Create Runbook'}</DialogTitle>
        <DialogDescription>
          {isEdit ? 'Update runbook content and metadata.' : 'Create a new runbook with markdown content.'}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4 overflow-y-auto max-h-[60vh]">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Title *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Runbook title" />
          </div>
          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger><SelectValue placeholder="Select category..." /></SelectTrigger>
              <SelectContent>
                {allCategories.map((cat) => (
                  <SelectItem key={cat} value={cat}>{categoryLabels[cat]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-2">
          <Label>Description</Label>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Brief description of the runbook" />
        </div>
        <div className="space-y-2">
          <Label>Content (Markdown) *</Label>
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="# Getting Started&#10;&#10;Write your runbook content here using Markdown..."
            rows={15}
            className="font-mono text-sm"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Tags (comma-separated)</Label>
            <Input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder="vpn, firewall, cisco" />
          </div>
          <div className="space-y-2">
            <Label>Status</Label>
            <div className="flex items-center gap-3 pt-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={isPublished}
                  onChange={(e) => setIsPublished(e.target.checked)}
                  className="rounded"
                />
                Published
              </label>
              {isPublished ? (
                <Badge className="bg-green-500/10 text-green-500 border-green-500/20">
                  <Globe className="h-3 w-3 mr-1" /> Published
                </Badge>
              ) : (
                <Badge variant="secondary">
                  <Lock className="h-3 w-3 mr-1" /> Draft
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} disabled={!title || !content || isPending}>
          {isPending ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Runbook'}
        </Button>
      </DialogFooter>
    </DialogContent>
  )

  if (isEdit) {
    return (
      <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose() }}>
        {dialogContent}
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button><Plus className="mr-2 h-4 w-4" /> New Runbook</Button>
      </DialogTrigger>
      {dialogContent}
    </Dialog>
  )
}

// ============================================
// Runbook View Dialog
// ============================================

function RunbookViewDialog({ runbookId, onClose }: { runbookId: string; onClose: () => void }) {
  const { data: runbookData, isLoading } = useQuery({
    queryKey: ['runbooks', runbookId],
    queryFn: () => apiClient.get<Runbook>(`/runbooks/${runbookId}`),
    enabled: !!runbookId,
  })

  const runbook = runbookData?.data || null

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh]">
        {isLoading || !runbook ? (
          <div className="space-y-4 py-8">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <DialogTitle className="text-xl">{runbook.title}</DialogTitle>
                  {runbook.description && (
                    <DialogDescription className="mt-1">{runbook.description}</DialogDescription>
                  )}
                </div>
                {runbook.isPublished ? (
                  <Badge className="bg-green-500/10 text-green-500 border-green-500/20">
                    <Globe className="h-3 w-3 mr-1" /> Published
                  </Badge>
                ) : (
                  <Badge variant="secondary">
                    <Lock className="h-3 w-3 mr-1" /> Draft
                  </Badge>
                )}
              </div>
            </DialogHeader>
            <div className="space-y-4 py-4 overflow-y-auto max-h-[60vh]">
              <div className="flex items-center gap-3 flex-wrap">
                {categoryBadge(runbook.category)}
                {runbook.tags.map((tag) => (
                  <Badge key={tag} variant="outline" className="text-xs">
                    <Tag className="h-3 w-3 mr-1" />{tag}
                  </Badge>
                ))}
                <span className="text-xs text-muted-foreground ml-auto">
                  v{runbook.version} | Updated {formatRelativeDate(runbook.updatedAt)}
                </span>
              </div>
              <div
                className="prose prose-sm dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(runbook.content || '') }}
              />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Category Sidebar
// ============================================

function CategorySidebar({
  selectedCategory,
  onSelect,
  stats,
}: {
  selectedCategory: string
  onSelect: (cat: string) => void
  stats: RunbookStats | null
}) {
  const categoryCounts = useMemo(() => {
    const map: Record<string, number> = {}
    if (stats?.byCategory) {
      for (const item of stats.byCategory) {
        map[item.category] = item.count
      }
    }
    return map
  }, [stats])

  return (
    <div className="space-y-1">
      <button
        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
          selectedCategory === 'all' ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'
        }`}
        onClick={() => onSelect('all')}
      >
        <span className="flex items-center justify-between">
          <span>All Runbooks</span>
          <span className="text-xs text-muted-foreground">{stats?.total || 0}</span>
        </span>
      </button>
      {allCategories.map((cat) => (
        <button
          key={cat}
          className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
            selectedCategory === cat ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'
          }`}
          onClick={() => onSelect(cat)}
        >
          <span className="flex items-center justify-between">
            <span>{categoryLabels[cat]}</span>
            <span className="text-xs text-muted-foreground">{categoryCounts[cat] || 0}</span>
          </span>
        </button>
      ))}
    </div>
  )
}

// ============================================
// Main Page
// ============================================

export function RunbooksPage() {
  const [search, setSearch] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [publishedFilter, setPublishedFilter] = useState<string>('all')
  const [viewingRunbookId, setViewingRunbookId] = useState<string | null>(null)
  const [editingRunbook, setEditingRunbook] = useState<Runbook | null>(null)
  const queryClient = useQueryClient()
  const { toast } = useToast()

  // Fetch runbooks
  const { data: runbooksData, isLoading } = useQuery({
    queryKey: ['runbooks', selectedCategory, publishedFilter, search],
    queryFn: () => apiClient.get<{ data: Runbook[] }>('/runbooks', {
      ...(selectedCategory !== 'all' && { category: selectedCategory }),
      ...(publishedFilter === 'published' && { isPublished: true }),
      ...(publishedFilter === 'draft' && { isPublished: false }),
      ...(search && { search }),
      limit: 100,
    }),
  })

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['runbooks', 'stats'],
    queryFn: () => apiClient.get<RunbookStats>('/runbooks/stats'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/runbooks/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['runbooks'] })
      toast({ title: 'Deleted', description: 'Runbook has been removed' })
    },
    onError: () => toast({ title: 'Error', description: 'Failed to delete runbook', variant: 'destructive' }),
  })

  const publishMutation = useMutation({
    mutationFn: (id: string) => apiClient.patch(`/runbooks/${id}/publish`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['runbooks'] })
      toast({ title: 'Updated', description: 'Published status toggled' })
    },
    onError: () => toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' }),
  })

  const stats = statsData?.data || null
  const rawRunbooks = runbooksData?.data?.data || runbooksData?.data || []
  const runbooks: Runbook[] = Array.isArray(rawRunbooks) ? rawRunbooks : []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Runbooks</h1>
          <p className="text-muted-foreground">Knowledge base and documentation for MSP teams</p>
        </div>
        <RunbookEditorDialog />
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Total Runbooks</p>
            </div>
            <div className="text-2xl font-bold mt-1">{stats?.total ?? 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-green-500" />
              <p className="text-xs text-muted-foreground">Published</p>
            </div>
            <div className="text-2xl font-bold text-green-500 mt-1">{stats?.published ?? 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-yellow-500" />
              <p className="text-xs text-muted-foreground">Drafts</p>
            </div>
            <div className="text-2xl font-bold text-yellow-500 mt-1">{stats?.draft ?? 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-blue-500" />
              <p className="text-xs text-muted-foreground">Categories</p>
            </div>
            <div className="text-2xl font-bold text-blue-500 mt-1">{stats?.byCategory?.length ?? 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="library">
        <TabsList>
          <TabsTrigger value="library">Library</TabsTrigger>
          <TabsTrigger value="recent">Recently Updated</TabsTrigger>
        </TabsList>

        <TabsContent value="library" className="space-y-4">
          <div className="flex gap-6">
            {/* Sidebar */}
            <div className="w-48 shrink-0">
              <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wider">Categories</p>
              <CategorySidebar
                selectedCategory={selectedCategory}
                onSelect={setSelectedCategory}
                stats={stats}
              />
            </div>

            {/* Main Content */}
            <div className="flex-1 space-y-4">
              {/* Filters */}
              <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search runbooks..."
                    className="pl-9"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <Select value={publishedFilter} onValueChange={setPublishedFilter}>
                  <SelectTrigger className="w-[150px]"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="draft">Drafts</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Runbook Grid */}
              {isLoading ? (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {[...Array(6)].map((_, i) => (
                    <Card key={i}>
                      <CardContent className="p-6">
                        <Skeleton className="h-5 w-3/4 mb-3" />
                        <Skeleton className="h-4 w-full mb-2" />
                        <Skeleton className="h-4 w-2/3" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : runbooks.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <BookOpen className="h-12 w-12 mb-4 opacity-50 text-muted-foreground" />
                    <p className="text-lg font-medium text-muted-foreground">No runbooks found</p>
                    <p className="text-sm text-muted-foreground">Create your first runbook to get started</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {runbooks.map((runbook: Runbook) => (
                    <Card
                      key={runbook.id}
                      className="cursor-pointer hover:border-primary/30 transition-colors group"
                      onClick={() => setViewingRunbookId(runbook.id)}
                    >
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between gap-2">
                          <CardTitle className="text-base line-clamp-1">{runbook.title}</CardTitle>
                          {runbook.isPublished ? (
                            <Globe className="h-4 w-4 text-green-500 shrink-0" />
                          ) : (
                            <Lock className="h-4 w-4 text-muted-foreground shrink-0" />
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        {runbook.description && (
                          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{runbook.description}</p>
                        )}
                        <div className="flex items-center gap-2 flex-wrap mb-3">
                          {categoryBadge(runbook.category)}
                          {runbook.tags.slice(0, 3).map((tag) => (
                            <Badge key={tag} variant="outline" className="text-xs">
                              <Tag className="h-3 w-3 mr-0.5" />{tag}
                            </Badge>
                          ))}
                          {runbook.tags.length > 3 && (
                            <span className="text-xs text-muted-foreground">+{runbook.tags.length - 3}</span>
                          )}
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">
                            v{runbook.version} | {formatRelativeDate(runbook.updatedAt)}
                          </span>
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7"
                              onClick={(e) => { e.stopPropagation(); setViewingRunbookId(runbook.id) }}
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7"
                              onClick={(e) => { e.stopPropagation(); setEditingRunbook(runbook) }}
                            >
                              <Edit3 className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7"
                              onClick={(e) => {
                                e.stopPropagation()
                                publishMutation.mutate(runbook.id)
                              }}
                            >
                              {runbook.isPublished ? <Lock className="h-3.5 w-3.5" /> : <Globe className="h-3.5 w-3.5" />}
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 text-destructive"
                              onClick={(e) => { e.stopPropagation(); deleteMutation.mutate(runbook.id) }}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="recent" className="space-y-4">
          {stats?.recentlyUpdated && stats.recentlyUpdated.length > 0 ? (
            <div className="space-y-2">
              {stats.recentlyUpdated.map((item) => (
                <Card
                  key={item.id}
                  className="cursor-pointer hover:border-primary/30 transition-colors"
                  onClick={() => setViewingRunbookId(item.id)}
                >
                  <CardContent className="flex items-center justify-between py-4">
                    <div className="flex items-center gap-3">
                      <BookOpen className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{item.title}</span>
                    </div>
                    <span className="text-sm text-muted-foreground">{formatRelativeDate(item.updatedAt)}</span>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <BookOpen className="h-12 w-12 mb-4 opacity-50 text-muted-foreground" />
                <p className="text-lg font-medium text-muted-foreground">No recent updates</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* View Dialog */}
      {viewingRunbookId && (
        <RunbookViewDialog
          runbookId={viewingRunbookId}
          onClose={() => setViewingRunbookId(null)}
        />
      )}

      {/* Edit Dialog */}
      {editingRunbook && (
        <RunbookEditorDialog
          runbook={editingRunbook}
          onClose={() => setEditingRunbook(null)}
        />
      )}
    </div>
  )
}
